import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import ReportFilters, { formatReportMoney } from './ReportFilters';
import {
  downloadRepPerformanceCsv,
  getRepPerformanceReport,
  RepPerformanceReport,
  ReportQuery,
} from '../../api/reportsService';

const RepPerformanceReportPage = () => {
  const [query, setQuery] = useState<ReportQuery>({ period: '1m' });
  const [report, setReport] = useState<RepPerformanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const data = await getRepPerformanceReport(query);
    if (!data) setError('Could not load rep performance report.');
    setReport(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [query]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadRepPerformanceCsv(query);
    } catch {
      setError('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link to="/reports" className="text-sm text-torch-primary hover:underline">← Reports</Link>
          <h1 className="mt-1 text-2xl font-semibold text-gray-900">Rep performance</h1>
          <p className="text-sm text-gray-500">Proposal activity and signed revenue by sales rep.</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || loading}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <PageCard className="mb-6">
        <ReportFilters query={query} onChange={setQuery} showStatus={false} />
      </PageCard>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <PageCard>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !report?.reps.length ? (
          <p className="text-sm text-gray-500">No data for this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Sales rep</th>
                  <th className="py-2 pr-4">Draft</th>
                  <th className="py-2 pr-4">Sent</th>
                  <th className="py-2 pr-4">Signed</th>
                  <th className="py-2 pr-4">Signed revenue</th>
                  <th className="py-2">Sent value</th>
                </tr>
              </thead>
              <tbody>
                {report.reps.map((row) => (
                  <tr key={row.sales_rep_id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium text-gray-900">{row.sales_rep}</td>
                    <td className="py-3 pr-4 text-gray-700">{row.draft_count}</td>
                    <td className="py-3 pr-4 text-gray-700">{row.sent_count}</td>
                    <td className="py-3 pr-4 text-gray-700">{row.signed_count}</td>
                    <td className="py-3 pr-4 font-medium text-gray-900">{formatReportMoney(row.signed_value)}</td>
                    <td className="py-3 text-gray-600">{formatReportMoney(row.sent_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default RepPerformanceReportPage;
